// Libraries
import { useTranslation } from "react-i18next";

// Components
import { Button, Divider, IconButton, Stack, Typography } from "@mui/material";
import {
  CalendarMonthOutlined as CalendarIcon,
  CheckCircleOutline as SuccessIcon,
  Close as CloseIcon,
  HighlightOff as ErrorIcon,
  LocationOnOutlined as LocationIcon,
  NotificationsActiveOutlined as NotificationIcon,
  PostAddOutlined as NoteIcon,
  StarOutline as ReasonIcon,
} from "@mui/icons-material";
import { getLocalizedDateCard } from "Components/Language/Common";

// Styles
import { styles } from "./styles";

const VARIANT_CONFIG = {
  default: {
    HeaderIcon: CalendarIcon,
    headerColor: "primary.main",
    showCloseButton: false,
  },
  success: {
    HeaderIcon: SuccessIcon,
    headerColor: "primary.main",
    showCloseButton: true,
  },
  error: {
    HeaderIcon: ErrorIcon,
    headerColor: "error.main",
    showCloseButton: false,
  },
};

const DetailRow = ({ icon: Icon, children, supportingText }) => (
  <Stack direction="row" spacing={1.5}>
    <Icon sx={styles.detailIcon} />
    <Stack>
      <Typography variant="body1">{children}</Typography>
      {supportingText && (
        <Typography variant="caption" color="text.secondary">
          {supportingText}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const DateCard = ({ dateCard }) => (
  <Stack spacing={0.5} sx={styles.dateCard}>
    <Typography sx={styles.dateLabel}>{dateCard.dateLabel}</Typography>
    <Typography sx={styles.day}>{dateCard.day}</Typography>
    <Typography sx={styles.month}>{dateCard.month}</Typography>
    <Divider flexItem />
    <Typography sx={styles.time}>{dateCard.time}</Typography>
  </Stack>
);

const AppointmentStatusContent = ({
  onClose = () => {},
  title,
  statusVariant = "default",
  patient,
  appointment,
  isMBS = false,
  reasonText,
  clinicMessage,
  statusMessage,
  showAppointmentSummary = true,
  actions = [],
}) => {
  const { t } = useTranslation();
  const { HeaderIcon, headerColor, showCloseButton } =
    VARIANT_CONFIG[statusVariant] || VARIANT_CONFIG.default;
  const patientName = `${patient.firstName} ${patient.lastName}`;
  const dateCard = getLocalizedDateCard(
    appointment?.date,
    appointment?.startTime,
    t
  );
  const patientContact = patient?.primaryPhone;
  const notificationText =
    patientContact || t("mbsAppointments.patientNoContact");

  return (
    <Stack spacing={1.5}>
      <Stack
        direction="row"
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1}>
          <HeaderIcon sx={{ color: headerColor }} />
          <Typography
            id="appointment-status-modal-title"
            color={headerColor}
            sx={styles.title}
          >
            {title}
          </Typography>
        </Stack>
        {showCloseButton && (
          <IconButton
            aria-label="close appointment status modal"
            onClick={onClose}
            size="small"
            sx={{ p: 0 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={2.5}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack spacing={1} sx={styles.detailsColumn}>
          <Typography variant="h6" color="text.primary" sx={styles.patientName}>
            {patientName}
          </Typography>
          {statusMessage && (
            <Typography variant="body1">{statusMessage}</Typography>
          )}
          {showAppointmentSummary && (
            <Stack spacing={1}>
              {isMBS && (
                <DetailRow icon={NotificationIcon}>
                  {notificationText}
                </DetailRow>
              )}
              <DetailRow
                icon={LocationIcon}
                supportingText={appointment?.clinicName}
              >
                {appointment?.clinicAddress}
              </DetailRow>
              {reasonText && (
                <DetailRow icon={ReasonIcon}>{reasonText}</DetailRow>
              )}
              {appointment?.notes && (
                <DetailRow icon={NoteIcon}>{appointment?.notes}</DetailRow>
              )}
            </Stack>
          )}
        </Stack>

        {showAppointmentSummary && <DateCard dateCard={dateCard} />}
      </Stack>

      {showAppointmentSummary && clinicMessage && (
        <Stack sx={styles.clinicMessage}>{clinicMessage}</Stack>
      )}

      {actions.length > 0 && (
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1.5}
          sx={styles.actions}
        >
          {actions.map(
            (
              {
                label,
                onClick = () => {},
                variant = "text",
                startIcon,
                disabled = false,
              },
              index
            ) => (
              <Button
                key={`${label}-${index}`}
                onClick={onClick}
                variant={variant}
                startIcon={startIcon}
                disabled={disabled}
              >
                {label}
              </Button>
            )
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default AppointmentStatusContent;
